import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import NavigationSidebar from '../../components/NavigationSidebar';
import Icon from '../../components/AppIcon';
import UserActionMetrics from './components/UserActionMetrics';
import FrictionPointHeatmap from './components/FrictionPointHeatmap';
import ConversionFunnelAnalysis from './components/ConversionFunnelAnalysis';
import AgentAdoptionTrends from './components/AgentAdoptionTrends';

const AnalyticsDashboard = () => {
  const { user } = useAuth();
  const [dateRange, setDateRange] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(null);

  useEffect(() => {
    // Initial load
    setLoading(false);

    // Set up auto-refresh every 5 minutes
    const interval = setInterval(() => {
      setRefreshInterval(Date.now());
    }, 300000);

    return () => clearInterval(interval);
  }, []);

  const dateRangeOptions = [
    { value: '24h', label: 'Last 24 Hours' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' },
    { value: 'all', label: 'All Time' }
  ];

  const handleExportData = () => {
    // Export functionality placeholder
    console.log('Exporting analytics data...');
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-background">
        <NavigationSidebar />
        <div className="flex-1 flex items-center justify-center ml-0 lg:ml-60">
          <p className="text-muted-foreground">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <NavigationSidebar />
      <main className="flex-1 ml-0 lg:ml-60 pb-20 lg:pb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground font-heading">
                Analytics Dashboard
              </h1>
              <p className="text-muted-foreground mt-1 font-caption">
                Comprehensive insights into user behavior, agent performance, and business metrics
              </p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e?.target?.value)}
                className="px-4 py-2 bg-card border border-border rounded-lg text-foreground font-caption focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {dateRangeOptions?.map((option) => (
                  <option key={option?.value} value={option?.value}>
                    {option?.label}
                  </option>
                ))}
              </select>
              <button
                onClick={handleExportData}
                className="px-4 py-2 bg-primary text-white rounded-lg font-caption font-medium hover:bg-primary/90 transition-colors flex items-center gap-2"
              >
                <Icon name="Download" size={18} />
                Export
              </button>
            </div>
          </div>

          {/* User Action Metrics */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Activity" size={24} color="var(--color-primary)" />
              <h2 className="text-2xl font-semibold text-foreground font-heading">
                User Action Metrics
              </h2>
            </div>
            <UserActionMetrics dateRange={dateRange} refreshTrigger={refreshInterval} />
          </section>

          {/* Friction Point Heatmaps */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Icon name="AlertTriangle" size={24} color="var(--color-orange-500)" />
              <h2 className="text-2xl font-semibold text-foreground font-heading">
                Friction Point Heatmaps
              </h2>
            </div>
            <FrictionPointHeatmap dateRange={dateRange} refreshTrigger={refreshInterval} />
          </section>

          {/* Conversion Funnel Analysis */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Filter" size={24} color="var(--color-blue-500)" />
              <h2 className="text-2xl font-semibold text-foreground font-heading">
                Conversion Funnel Analysis
              </h2>
            </div>
            <ConversionFunnelAnalysis dateRange={dateRange} refreshTrigger={refreshInterval} />
          </section>

          {/* Agent Adoption Trends */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Icon name="TrendingUp" size={24} color="var(--color-green-500)" />
              <h2 className="text-2xl font-semibold text-foreground font-heading">
                Agent Adoption Trends
              </h2>
            </div>
            <AgentAdoptionTrends dateRange={dateRange} refreshTrigger={refreshInterval} />
          </section>
        </div>
      </main>
    </div>
  );
};

export default AnalyticsDashboard;